import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Loader2, LogOut } from 'lucide-react'
import clsx from 'clsx'
import type { Channel } from '@/lib/types'
import { useStore } from '@/lib/store'
import { trackEvent } from '@/lib/posthog'

export function DisconnectChannelButton({
  channel,
  compact = false,
}: {
  channel: Channel
  compact?: boolean
}) {
  const removeChannel = useStore((state) => state.removeChannel)
  const [confirming, setConfirming] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoreFocus, setRestoreFocus] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!confirming && restoreFocus) {
      triggerRef.current?.focus()
      setRestoreFocus(false)
    }
  }, [confirming, restoreFocus])

  const cancelConfirmation = () => {
    setRestoreFocus(true)
    setConfirming(false)
  }

  const disconnect = async () => {
    setDisconnecting(true)
    setError(null)
    try {
      await removeChannel(channel.id)
      trackEvent('account_disconnected', { platform: channel.platform })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not disconnect this account.')
      setDisconnecting(false)
      setRestoreFocus(true)
      setConfirming(false)
    }
  }

  if (confirming) {
    if (compact) {
      return (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-md px-2 py-1 text-[11px] font-medium text-ink-500 transition hover:bg-panel hover:text-ink-900"
            onClick={cancelConfirmation}
            disabled={disconnecting}
            autoFocus
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md bg-accent-red px-2 py-1 text-[11px] font-semibold text-white transition hover:bg-accent-red/90 disabled:opacity-60"
            onClick={() => void disconnect()}
            disabled={disconnecting}
            aria-label={`Confirm log out of ${channel.name}`}
          >
            {disconnecting && <Loader2 size={11} className="animate-spin" />}
            Log out
          </button>
        </div>
      )
    }

    return (
      <div className="w-full border-t border-line pt-3">
        <p className="text-xs text-ink-600">
          Disconnect {channel.name}? Scheduled posts for it will be removed.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            className="btn-ghost px-2.5 py-1.5 text-xs"
            onClick={cancelConfirmation}
            disabled={disconnecting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn bg-accent-red px-2.5 py-1.5 text-xs text-white hover:bg-accent-red/90 disabled:opacity-60"
            onClick={() => void disconnect()}
            disabled={disconnecting}
          >
            {disconnecting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <LogOut size={14} />
            )}
            Disconnect
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={clsx(compact ? '' : 'w-full border-t border-line pt-3')}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setError(null)
          setConfirming(true)
        }}
        className={clsx(
          'inline-flex items-center gap-1.5 font-medium text-accent-red transition hover:text-accent-red/80',
          compact
            ? 'rounded-md px-2 py-1 text-[11px] hover:bg-accent-red/10'
            : 'text-xs',
        )}
        aria-label={compact ? `Log out of ${channel.name}` : `Disconnect ${channel.name}`}
      >
        <LogOut size={compact ? 12 : 14} />
        {compact ? 'Log out' : 'Disconnect'}
      </button>
      {error && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-accent-red" role="alert">
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}
